import { useMemo, useCallback } from 'react'
import { IntervalDataEntry, IntervalReportSettings } from '@/hooks'
import { useActiveManagementMetersQuery } from '../../hooks/useActiveManagementMetersQuery'
import { useActiveManagementSubmetersQuery } from '../../hooks/useActiveManagementSubmetersQuery'
import { numberWithCommas } from '@/utils/numberWithCommas'
import { getStrokeHexValue } from '@/components/organisms/MultiChart/getStrokeHexValue'
import { useDataCompleteness } from '../../hooks/useDataCompleteness'
import { getEntryId } from '../../utils/getEntryId'
import { ActiveManagementFiltersValues } from '@/utils/constants'

const formatValue = (val: number) => numberWithCommas(val)

export type IntervalSummaryData = {
    metricLabelKey: string
    metricValue: string
    contextMeterName?: string
    contextMeterId?: number
    contextMeterColor?: string
    completenessPercentage: number
    minimumReportedMeters: number
    totalMeters: number
    hasData: boolean
}

type AggregateSummaryResult = {
    metricLabelKey: string
    metricValue: string
    actual: number
}

type CompareSummaryResult = AggregateSummaryResult & {
    winningMeter?: {
        meterId: number
        actual: number
    }
}

const createEntryValueGetter =
    (dataType: IntervalReportSettings['dataType']) =>
    (entry: IntervalDataEntry): number | undefined => {
        switch (dataType) {
            case 'usage':
                return entry.usage
            case 'demandMax':
                return entry.demandMax
            case 'demandAverage':
                return entry.demandAverage
            default:
                return undefined
        }
    }

const buildMeterOrder = (
    data: IntervalDataEntry[],
    display: IntervalReportSettings['display']
) => {
    const cohort = new Set<number>()
    data.forEach((entry) => {
        const id = getEntryId(entry, display)
        if (id !== undefined) {
            cohort.add(id)
        }
    })
    return Array.from(cohort)
}

const summarizeAggregate = (
    data: IntervalDataEntry[],
    dataType: IntervalReportSettings['dataType'],
    hasEntryValue: (entry: IntervalDataEntry) => boolean
): AggregateSummaryResult => {
    const validData = data.filter(hasEntryValue)
    const actual = validData.length

    const getValue = createEntryValueGetter(dataType)

    let result: number
    if (dataType === 'demandAverage' || dataType === 'demandMax') {
        // For demandAverage and demandMax: find the highest value from all datasets
        result = validData.reduce((max, curr) => {
            const value = getValue(curr) ?? -Infinity
            return Math.max(max, value)
        }, -Infinity)
    } else {
        // For usage: sum all values
        result = validData.reduce((acc, curr) => acc + (getValue(curr) ?? 0), 0)
    }

    let metricLabelKey: string
    switch (dataType) {
        case 'demandAverage':
            metricLabelKey = 'active_management.interval.summary.demand_average'
            break
        case 'demandMax':
            metricLabelKey = 'active_management.interval.summary.demand_max'
            break
        default:
            metricLabelKey = 'active_management.interval.summary.total_usage'
            break
    }

    return {
        metricLabelKey,
        metricValue:
            actual > 0 && result !== -Infinity
                ? formatValue(Number(result.toFixed(2)))
                : '-',
        actual,
    }
}

const summarizeCompare = (
    data: IntervalDataEntry[],
    dataType: IntervalReportSettings['dataType'],
    hasEntryValue: (entry: IntervalDataEntry) => boolean,
    display: IntervalReportSettings['display']
): CompareSummaryResult => {
    const validData = data.filter((entry) => {
        const id = getEntryId(entry, display)
        return hasEntryValue(entry) && id !== undefined
    })

    const getValue = createEntryValueGetter(dataType)

    let bestValue = -Infinity
    let winningMeter: CompareSummaryResult['winningMeter']

    if (dataType === 'demandAverage' || dataType === 'demandMax') {
        // For demandAverage and demandMax: find the highest value per meter,
        // then compare those highest values across meters
        const meterMaxValues = new Map<number, { max: number; count: number }>()

        validData.forEach((entry) => {
            const meterId = getEntryId(entry, display) as number
            const value = getValue(entry) ?? -Infinity

            const existing = meterMaxValues.get(meterId)
            if (existing) {
                existing.max = Math.max(existing.max, value)
                existing.count += 1
            } else {
                meterMaxValues.set(meterId, { max: value, count: 1 })
            }
        })

        // Find the meter with the highest max value
        meterMaxValues.forEach((totals, meterId) => {
            if (totals.max > bestValue) {
                bestValue = totals.max
                winningMeter = {
                    meterId,
                    actual: totals.count,
                }
            }
        })
    } else {
        // For usage: sum values for each meter, then find the meter with the highest total
        const meterTotals = new Map<number, { sum: number; count: number }>()

        validData.forEach((entry) => {
            const meterId = getEntryId(entry, display) as number
            const value = getValue(entry) ?? 0

            const existing = meterTotals.get(meterId)
            if (existing) {
                existing.sum += value
                existing.count += 1
            } else {
                meterTotals.set(meterId, { sum: value, count: 1 })
            }
        })

        // Find the meter with the highest total
        meterTotals.forEach((totals, meterId) => {
            if (totals.sum > bestValue) {
                bestValue = totals.sum
                winningMeter = {
                    meterId,
                    actual: totals.count,
                }
            }
        })
    }

    const metricValue =
        bestValue !== -Infinity
            ? formatValue(Number(bestValue.toFixed(2)))
            : '-'
    const metricLabelKey =
        dataType === 'demandAverage'
            ? 'active_management.interval.summary.highest_demand_average'
            : dataType === 'demandMax'
              ? 'active_management.interval.summary.highest_demand_max'
              : 'active_management.interval.summary.highest_total_usage'

    return {
        metricLabelKey,
        metricValue,
        actual: winningMeter?.actual ?? 0,
        winningMeter,
    }
}

export const useIntervalSummaryData = (
    activeFilters: ActiveManagementFiltersValues,
    reportSettings: IntervalReportSettings,
    data: IntervalDataEntry[] | undefined
): IntervalSummaryData => {
    const { data: meters } = useActiveManagementMetersQuery({ activeFilters })
    const { data: subMeters } = useActiveManagementSubmetersQuery({
        activeFilters,
    })

    const meterMap = useMemo(() => {
        const metersDisplay =
            reportSettings.display === 'subMeters' ? subMeters : meters
        if (!metersDisplay) return new Map<number, string>()
        return new Map(
            metersDisplay.map((meter) => [meter.meterId, meter.meterName])
        )
    }, [meters, subMeters, reportSettings.display])

    const { dataType, dataGrouping, display } = reportSettings

    const selectedMeters = useMemo(() => {
        return display === 'subMeters'
            ? (activeFilters.subMeter ?? [])
            : activeFilters.meter
    }, [display, activeFilters.meter, activeFilters.subMeter])

    const allMeters = useMemo(() => {
        return display === 'subMeters' ? subMeters : meters
    }, [display, meters, subMeters])

    const totalMeters =
        selectedMeters.length > 0
            ? selectedMeters.length
            : (allMeters?.length ?? 0)

    const getEntryValue = useCallback(
        (entry: IntervalDataEntry) => createEntryValueGetter(dataType)(entry),
        [dataType]
    )

    const dataCompletenessMap = useDataCompleteness({
        data: data || [],
        totalMeters,
        dataGrouping,
        getValue: getEntryValue,
        display,
    })

    const minimumReportedMeters = useMemo(() => {
        if (dataCompletenessMap.size === 0) return 0

        let minimum: number | undefined
        for (const completeness of dataCompletenessMap.values()) {
            if (
                minimum === undefined ||
                completeness.metersWithData < minimum
            ) {
                minimum = completeness.metersWithData
            }
        }

        return minimum ?? 0
    }, [dataCompletenessMap])

    const completenessPercentage = useMemo(() => {
        return totalMeters > 0
            ? Math.round(
                  Math.max(
                      0,
                      Math.min(100, (minimumReportedMeters / totalMeters) * 100)
                  )
              )
            : 0
    }, [minimumReportedMeters, totalMeters])

    return useMemo(() => {
        const hasEntryValue = (entry: IntervalDataEntry) =>
            getEntryValue(entry) !== undefined

        if (!data || data.length === 0) {
            return {
                metricLabelKey: '',
                metricValue: '',
                completenessPercentage: 0,
                minimumReportedMeters: 0,
                totalMeters,
                hasData: false,
            }
        }

        let contextMeterName: string | undefined = undefined
        let contextMeterColor: string | undefined = undefined
        const meterOrder = buildMeterOrder(data, display)

        let winningMeter: CompareSummaryResult['winningMeter'] | undefined
        let summary: AggregateSummaryResult
        if (dataGrouping === 'aggregate') {
            summary = summarizeAggregate(data, dataType, hasEntryValue)
        } else {
            const compareSummary = summarizeCompare(
                data,
                dataType,
                hasEntryValue,
                display
            )
            summary = compareSummary
            winningMeter = compareSummary.winningMeter
        }

        if (winningMeter) {
            const meterId = winningMeter.meterId
            contextMeterName = meterMap.get(meterId) || meterId.toString()
            const colorIndex = meterOrder.indexOf(meterId)
            if (colorIndex >= 0) {
                contextMeterColor = getStrokeHexValue(colorIndex)
            }
        }

        return {
            metricLabelKey: summary.metricLabelKey,
            metricValue: summary.metricValue,
            contextMeterName,
            contextMeterColor,
            completenessPercentage,
            minimumReportedMeters,
            totalMeters,
            hasData: true,
        }
    }, [
        data,
        dataType,
        dataGrouping,
        display,
        meterMap,
        minimumReportedMeters,
        completenessPercentage,
        totalMeters,
        getEntryValue,
    ])
}
